/**
 * G12 / V32: the all-projects overview (`/overview`) and the per-project summary strip on the
 * project tab. Both render `OverviewTile`s derived from the Lanes model (lib/overview-model.ts).
 * Every number and line is a link to where it lives (tab, session, inspector).
 */
import { Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useDigests } from '../../lib/digests.js'
import { isLiveRun } from '../../lib/lanes-model.js'
import { buildOverview, type OverviewTile } from '../../lib/overview-model.js'
import { waitingText } from '../../lib/project-list.js'
import {
  useBuilds,
  useConnection,
  useInbox,
  useLandings,
  useMachines,
  useProjects,
  useRuns,
  useTasks,
  useWorkstreams,
} from '../../lib/queries.js'
import { useNeedsYou } from '../inbox/useNeedsYou.js'
import { useLivePhases } from '../lanes/LiveRuns.js'
import { Ago, Empty, PageHead, SkeletonRows } from '../ui.js'
import './overview.css'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

function useMinute() {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [])
  return now
}

export function TileFacts({ tile }: { tile: OverviewTile }) {
  return (
    <>
      <div className="ov-nums">
        <Link
          to="/p/$projectId"
          params={{ projectId: tile.projectId }}
          className={`ov-num${tile.needs ? ' is-hot' : ''}`}
          aria-label={`${plural(tile.needs, 'decision')} need you in ${tile.name}`}
        >
          <b>{tile.needs}</b> need you
        </Link>
        <span className="ov-num">
          <b>{tile.running}</b> running
        </span>
        <span className="ov-num">
          <b>{tile.queued}</b> queued
        </span>
        <span className="ov-num">
          <b>{tile.landedToday}</b> landed today
        </span>
      </div>
      {tile.agents.length ? (
        <ul className="ov-agents" aria-label="Running agents">
          {tile.agents.map((a) => (
            <li key={a.runId}>
              <Link to="/s/$runId" params={{ runId: a.runId }} className="ov-agent">
                <span className="ov-who">{a.label}</span>
                <span className="ov-line">{a.waiting ? waitingText(a.line) : a.line}</span>
              </Link>
            </li>
          ))}
          {tile.moreAgents ? (
            <li className="ov-fact">
              <Link to="/p/$projectId" params={{ projectId: tile.projectId }}>
                +{tile.moreAgents} more {tile.moreAgents === 1 ? 'agent' : 'agents'}
              </Link>
            </li>
          ) : null}
        </ul>
      ) : null}
      {tile.lastLanded ? (
        <p className="ov-fact">
          <span>Last landed </span>
          <Link
            to="/p/$projectId"
            params={{ projectId: tile.projectId }}
            search={{ i: `task:${tile.lastLanded.taskId}` } as never}
          >
            {tile.lastLanded.title}
          </Link>{' '}
          <Ago ts={tile.lastLanded.at} />
        </p>
      ) : null}
      {tile.nextDigest ? (
        <p className="ov-fact">
          <span>Next in digest </span>
          <Link to="/settings/notifications">{tile.nextDigest.title}</Link>
        </p>
      ) : null}
      {tile.degradedReason ? <p className="ov-note">{tile.degradedReason}</p> : null}
    </>
  )
}

export function OverviewTileCard({ tile }: { tile: OverviewTile }) {
  return (
    <section className={`ov-tile is-${tile.health}`} aria-label={tile.name}>
      <div className="ov-head">
        <h2 className="ov-name">
          <Link to="/p/$projectId" params={{ projectId: tile.projectId }}>
            {tile.name}
          </Link>
        </h2>
        <span className="ov-health">{tile.healthLabel}</span>
      </div>
      <TileFacts tile={tile} />
    </section>
  )
}

/** The overview tiles (one per project) and the live-phase probes they need mounted. */
export function useOverview() {
  const projects = useProjects()
  const workstreams = useWorkstreams()
  const tasks = useTasks({})
  const runs = useRuns()
  const inbox = useInbox()
  const landings = useLandings()
  const builds = useBuilds()
  const machines = useMachines()
  const conn = useConnection()
  const digests = useDigests()
  const now = useMinute()
  const needs = useNeedsYou()
  const liveRuns = useMemo(() => (runs.data ?? []).filter(isLiveRun), [runs.data])
  const { phases, probes } = useLivePhases(liveRuns)
  const overview = useMemo(
    () =>
      buildOverview({
        projects: projects.data ?? [],
        workstreams: workstreams.data ?? [],
        tasks: tasks.data ?? [],
        runs: runs.data ?? [],
        inbox: inbox.data ?? [],
        landings: landings.data ?? [],
        builds: builds.data ?? [],
        machines: machines.data ?? [],
        live: phases,
        connection: conn,
        digests: digests.data ?? [],
        // One count source (lib/needs-you.ts): tiles and totals match the badges and the Inbox.
        needsYou: needs.of,
        needsYouTotal: needs.total,
        now,
      }),
    [
      needs,
      projects.data,
      workstreams.data,
      tasks.data,
      runs.data,
      inbox.data,
      landings.data,
      builds.data,
      machines.data,
      phases,
      conn,
      digests.data,
      now,
    ],
  )
  return { overview, probes, needs, pending: projects.isPending }
}

/** `embedded`: a section of Home (no page heading; Home shows the totals). */
export function OverviewPage({ embedded }: { embedded?: boolean } = {}) {
  const { overview, probes, pending } = useOverview()
  const projects = { isPending: pending }
  return (
    <div className={embedded ? 'ov-page is-embedded' : 'ov-page'}>
      {embedded ? null : <PageHead title="Overview" />}
      {projects.isPending ? (
        <SkeletonRows n={4} />
      ) : overview.tiles.length === 0 ? (
        <Empty center title="No projects yet">
          Add a project to see its status here.
        </Empty>
      ) : (
        <>
          {embedded ? null : (
            <p className="ov-totals">
              <span>
                <strong>{overview.totals.needs}</strong> need you
              </span>
              <span>
                <strong>{overview.totals.running}</strong> running
              </span>
              <span>
                <strong>{overview.totals.projects}</strong>{' '}
                {overview.totals.projects === 1 ? 'project' : 'projects'}
              </span>
            </p>
          )}
          <div className="ov-grid">
            {overview.tiles.map((t) => (
              <OverviewTileCard key={t.projectId} tile={t} />
            ))}
          </div>
        </>
      )}
      {probes}
    </div>
  )
}

/** Project tab header: the same tile facts in one strip above the lanes (reuses the Lanes model). */
export function ProjectSummary({ tile }: { tile: OverviewTile }) {
  return (
    <div className="ov-summary" role="region" aria-label={`${tile.name} summary`}>
      <span className="ov-health">{tile.healthLabel}</span>
      <TileFacts tile={tile} />
    </div>
  )
}
